import { BlobServiceClient, ContainerClient, BlockBlobClient } from '@azure/storage-blob';
import { azureBlobConfig } from '../authConfig';
import { CalendarEvent } from '../types/calendar';

export class AzureBlobService {
    private containerClient: ContainerClient | null = null;
    private isInitialized = false;

    private initialize(): void {
        if (this.isInitialized) return;

        try {
            const { accountName, containerName, sasToken } = azureBlobConfig;

            if (!accountName || !containerName || !sasToken) {
                console.warn('Azure Blob Storage not configured properly. Missing required environment variables.');
                return;
            }

            const blobServiceClient = new BlobServiceClient(
                `https://${accountName}.blob.core.windows.net/?${sasToken}`
            );

            this.containerClient = blobServiceClient.getContainerClient(containerName);
            this.isInitialized = true;
        } catch (error) {
            console.error('Failed to initialize Azure Blob Storage:', error);
        }
    }

    async saveEvents(events: CalendarEvent[]): Promise<boolean> {
        this.initialize();

        if (!this.containerClient) {
            console.warn('Azure Blob Storage not available, cannot save events');
            return false;
        }

        try {
            const blobClient: BlockBlobClient = this.containerClient.getBlockBlobClient(azureBlobConfig.blobName);

            // Serialize events for storage
            const serializedEvents = JSON.stringify(events, null, 2);

            // Check if this is the first time saving (blob doesn't exist)
            const exists = await blobClient.exists();
            const isFirstSave = !exists;

            await blobClient.upload(serializedEvents, serializedEvents.length, {
                blobHTTPHeaders: {
                    blobContentType: 'application/json',
                    blobCacheControl: 'no-cache'
                },
                metadata: {
                    'lastModified': new Date().toISOString(),
                    'source': 'thesis-calendar',
                    'isFirstSave': isFirstSave.toString()
                }
            });

            if (isFirstSave) {
                console.log('🎉 Calendar data successfully initialized in Azure Blob Storage');
            } else {
                console.log(`Events successfully saved to Azure Blob Storage (${events.length} events)`);
            }
            return true;
        } catch (error) {
            console.error('Error saving events to Azure Blob Storage:', error);
            // Log more details about the error
            if (error instanceof Error) {
                console.error('Error details:', {
                    message: error.message,
                    name: error.name,
                    stack: error.stack
                });
            }
            return false;
        }
    }

    async loadEvents(): Promise<CalendarEvent[] | null> {
        this.initialize();

        if (!this.containerClient) {
            console.warn('Azure Blob Storage not available, cannot load events');
            return null;
        }

        try {
            const blobClient: BlockBlobClient = this.containerClient.getBlockBlobClient(azureBlobConfig.blobName);

            // Check if blob exists
            const exists = await blobClient.exists();
            if (!exists) {
                console.log('Events blob does not exist yet - this is normal for first-time setup');
                return null;
            }

            // Download the blob content
            const downloadResponse = await blobClient.download();

            // For browser compatibility, use the downloaded content
            const downloaded = await downloadResponse.blobBody;
            const text = await downloaded!.text();

            const events = JSON.parse(text);

            // Convert date strings back to Date objects
            const parsedEvents: CalendarEvent[] = events.map((event: any) => ({
                ...event,
                start: new Date(event.start),
                end: new Date(event.end),
                createdAt: new Date(event.createdAt),
                updatedAt: new Date(event.updatedAt),
            }));

            console.log(`Events successfully loaded from Azure Blob Storage (${parsedEvents.length} events)`);
            return parsedEvents;
        } catch (error) {
            console.error('Error loading events from Azure Blob Storage:', error);
            // Log more details about the error
            if (error instanceof Error) {
                console.error('Error details:', {
                    message: error.message,
                    name: error.name,
                    stack: error.stack
                });

                // Check if it's a CORS error
                if (error.message.includes('CORS') || error.message.includes('Access-Control')) {
                    console.error('🚨 CORS Error: Please configure CORS settings in your Azure Storage Account');
                    console.error('📖 See AZURE_BLOB_SETUP.md for detailed instructions');
                }
            }
            return null;
        }
    }

    // No longer needed since we're using blobBody for browser compatibility

    isAvailable(): boolean {
        this.initialize();
        return this.isInitialized && this.containerClient !== null;
    }
}

// Export a singleton instance
export const azureBlobService = new AzureBlobService();
